/* eslint-disable */
import axios from "axios";
import {
    ActiveSio,
    AllUsersReportRow,
    ApiMessage,
    CardUserdata,
    Com4ReconciliationReport,
    Com4InventoryRunReport,
    Com4ManualHistoryEntry,
    Com4OperationEvent,
    Company,
    CountryCode,
    DateTime,
    ExportserviceHeaderRow,
    GeoPoint,
    GuiUserlisting,
    HmsregIntegrationCompany,
    InOutAvailableLocation,
    InvoicingExceptedTerminal,
    JLocation,
    PendingCardConfirm,
    PendingPerson,
    PendingPhoneconfirm,
    PersonConfig,
    PersonMergeResult,
    PhonelistReportRow,
    PhoneNumber,
    ProresultIntegrationCompanySettings,
    PendingCardReply,
    SimAndTerminalReportInfo,
    SimRow,
    SiteInOutReportLine,
    Terminal,
    TerminalConfigNext,
    TerminalWithoutSIM,
    TerminalGuiInfo,
    TerminalLocation,
    TerminalLocationArea,
    TerminalLocationCreate,
    TerminalLocationExportserviceInfo,
    TerminalLocationHmsregInfo,
    TerminalLocationWithStats,
    UserSiteReportRow,
    PersonmergeUndoInfo,
    PhoneCheckReplies,
    BillingInfoLocation,
    BillingInfoTerminal,
    SimReportRow,
    TerminalLocationRegcode,
    RegcodeCheckReply,
    CompanyAccount,
    AccountQuotas,
    InvoicingExceptedLocation,
    PurchaseOrder,
    TerminalLocationGeofence,
    PersonRegreminderLocation,
    MonthCountStat,
    SetRegreminderResponse,
    LastIn,
    RolesForCompany,
    FamocoDevice,
    RegReportResult,
    CompanyConfig,
    PendingEmailConfirmInfo,
    HmsregLocationStatus,
    HseGuideline,
    TerminalLocationShortInfo
} from "./models";
import {maybeNum, maybeStr, numOrNull, requireNum, requireStr} from "./utils";
import Routes from "./Routes";
import {UsrMsg} from "./usermessages";
import {UserRoleReportRow} from "../components/users/reports/UserRoleReportModel";
import {CardExpirationReportRow} from "@/components/users/reports/CardExpirationReportModel";
import UsersWithoutCardReportRow from "@/components/users/reports/UsersWithoutCardReportRow";
import {NativeApi, OS} from "@/modules/NativeBridge";
import SystemSettingsApi from "../components/systemsettings/Api";
import {NotificationConfig, NotificationSensor} from "@/modules/notificationmodels";
import {
    GuiUserlistingAndDateInterval,
    InfectionTraceRow, InfectionTraceXlsxParams, PersonMissingPhoneReportRow,
    PersonRefAndDateInterval,
    PersonsHseGuidelineConfirmedReportResult,
    TerminalLocationInfoDateInterval
} from "@/modules/reportmodels";
import {DataUseReport} from "@/modules/terminalDataUseReport";

export class PlayServerError extends Error{}

export class ForbiddenError extends Error{
    constructor() {
        super(`user is not allowed to perform request`);
    }
}

export class NotAuthenticated extends Error {
    private pNum: number;
    constructor(msg: ApiMessage | string | null = null) {
        if(msg instanceof ApiMessage) {
            super(msg.msg);
            this.pNum = msg.num;
        } else if(typeof msg == "string") {
            super( msg);
            this.pNum = 0;
        } else {
            super("Required authentication credentials not provided");
            this.pNum = 0;
        }
    }

    get num() {
        return this.pNum;
    }
}
export class BadRequest extends Error {
    private pNum: number;
    constructor(msg: ApiMessage | string | null = null) {
        if(msg instanceof ApiMessage) {
            super(msg.msg);
            this.pNum = msg.num;
        } else if(typeof msg == "string") {
            super(msg);
            this.pNum = 0;
        } else {
            super(msg != null ? msg : "Bad request");
            this.pNum = 0;
        }
    }
}

export class ServiceUnavailable extends Error {
    private pNum: number;
    constructor(msg: ApiMessage | string | null = null) {
        if(msg instanceof ApiMessage) {
            super(msg.msg);
            this.pNum = msg.num;
        } else if(typeof msg == "string") {
            super(msg);
            this.pNum = 0;
        } else {
            super(msg != null ? msg : "Service unavailable");
            this.pNum = 0;
        }
    }

    get num() {
        return this.pNum;
    }
}

/*
export class InvalidStatusError extends PlayServerError {
    constructor(status: number, url: string) {
        super(`Invalid response status "${status}" from server for request ${url}`);
    }
}
*/

export class InvalidResponseType extends PlayServerError {
    constructor(expectedType: string, gotType: string) {
        super(`Invalid response type. Expected ${expectedType}, got: ${gotType}`);
    }
}

const ax = axios.create();
ax.defaults.headers.post["Content-Type"] = "application/json";
ax.defaults.headers.common["Accept"] = "application/json, text/plain;q=0.5";

ax.defaults.validateStatus = (status) => status >= 200 && status < 400 || status == 400 || status == 401 || status == 403 || status == 503;

// Reform errors to my error types so they are easier to catch
ax.interceptors.response.use((response) => {
    if(response.status == 403) {
        return Promise.reject(new ForbiddenError());
    } else if(response.status == 401) {
        if(PlayServerClient.hasResponseApiMessage(response)) {
            return Promise.reject(new NotAuthenticated(ApiMessage.fromJson(response.data)));
        } else if(PlayServerClient.hasResponseMessage(response)) {
            return Promise.reject(new NotAuthenticated(response.data.message));
        }
        return Promise.reject(new NotAuthenticated());
    } else if(response.status === 400) {
        if(PlayServerClient.hasResponseApiMessage(response)) {
            return Promise.reject(new BadRequest(ApiMessage.fromJson(response.data)));
        } else if(PlayServerClient.hasResponseMessage(response)) {
            return Promise.reject(new BadRequest(response.data.message));
        }
        return Promise.reject(new BadRequest());
    } else if(response.status === 503) {
        if(PlayServerClient.hasResponseApiMessage(response)) {
            return Promise.reject(new ServiceUnavailable(ApiMessage.fromJson(response.data)));
        } else if(PlayServerClient.hasResponseMessage(response)) {
            return Promise.reject(new ServiceUnavailable(response.data.message));
        }
    }
    return response;
});

/**
 * Fetches and sends data from/to play server
 */
export default class PlayServerClient implements SystemSettingsApi {
    static hasResponseApiMessage(response: any) {
        return "application/json" === PlayServerClient.contentType(response) && typeof response.data.msg === "string" && typeof response.data.num === "number";
    }

    static hasResponseMessage(response: any) {
        return "application/json" === PlayServerClient.contentType(response) && typeof response.data.message === "string";
    }

    async fetchLocations(orgnum: number): Promise<TerminalLocationWithStats[]> {
        const url = Routes.terminalLocations(orgnum);

        const resp = await ax.get(url);
        if (resp.data instanceof Array) {
            const arr = resp.data as any[];
            return arr.map((tl) => TerminalLocationWithStats.fromJson(tl));
        } else {
            throw new InvalidResponseType("array", typeof resp.data);
        }
    }

    async terminalLocationCreate(loc: TerminalLocation, hmsregInfo: TerminalLocationHmsregInfo, exportserviceInfos: TerminalLocationExportserviceInfo[]): Promise<ApiMessage | TerminalLocationWithStats> {
        let createData = new TerminalLocationCreate(loc, hmsregInfo, exportserviceInfos);
        const url = Routes.terminalLocationcreate();
        const resp = await ax.post<any>(url, createData.toJson());
        PlayServerClient.assertJson(resp);
        if (PlayServerClient.hasResponseApiMessage(resp)) {
            return ApiMessage.fromJson(resp.data);
        }
        return TerminalLocationWithStats.fromJson(resp.data);
    }

    async fetchDefaultCompany(): Promise<Company | null> {
        const url = Routes.defaultCompany();
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            return Company.fromJson(resp.data);
        } else {
            return null;
        }
    }

    async fetchCompany(orgnum: number): Promise<Company> {
        const url = Routes.company(orgnum);
        const resp = await ax.get(url);
        return Company.fromJson(resp.data);
    }

    async getCompanyConfig(orgnum: number): Promise<CompanyConfig> {
        const url = Routes.company(orgnum) + "/config";
        const resp = await ax.get(url)
        PlayServerClient.assertJson(resp)
        return CompanyConfig.fromJson(resp.data);
    }

    async isHmsregActivated(orgnum: number): Promise<boolean> {
        const url = Routes.hmsregActivated(orgnum);
        const resp = await ax.get(url);
        return resp.data === true;
    }

    async exportserviceInfoList(orgnum: number, locid: number | null) {
        const url = Routes.exportserviceInfoList(orgnum);
        const resp = await ax.get(url, {params: {"locid": locid}});
        if (resp.data instanceof Array) {
            const arr = resp.data as any[];
            return arr.map((ei) => TerminalLocationExportserviceInfo.fromJson(ei));
        } else {
            throw new InvalidResponseType("array", typeof resp.data);
        }
    }

    async siteReport(companyOrgNum: number, locationId: number, from: DateTime, to: DateTime, inFilterOrgNum: number): Promise<SiteInOutReportLine[]> {
        const url = Routes.siteReport(companyOrgNum);
        const params = {
            locationId: locationId,
            from: from.toJSON(),
            to: to.toJSON(),
            filterOrgNum: inFilterOrgNum,
        };
        const resp = await ax.get(url, {
            params: params,
            headers: {
                "Accept": "application/json",
            }
        });
        if (resp.data instanceof Array) {
            const arr = resp.data as any[];
            return arr.map((r) => SiteInOutReportLine.fromJson(r));
        } else {
            throw new InvalidResponseType("array", typeof resp.data);
        }
    }

    async regReport(
        companyOrgNum: number,
        from: DateTime | null,
        to: DateTime | null,
        filterOrgnums: number[],
        filterLocationIds: number[],
        filterUserGuids: string[],
        page: number,
        ordering: number,
    ): Promise<RegReportResult> {
        const url = Routes.regReport(companyOrgNum);
        const params = {
            from: from != null ? from.toJSON() : null,
            to: to != null ? to.toJSON() : null,
        };
        if (filterOrgnums.length > 0) {
            params["filterOrgnums"] = filterOrgnums.join(",");
        }
        if (filterLocationIds.length > 0) {
            params["filterLocationIds"] = filterLocationIds.join(",")
        }
        if (filterUserGuids.length > 0) {
            params["filterUserGuids"] = filterUserGuids.join(",")
        }
        if (page != 1) {
            params["page"] = page;
        }
        if (ordering != 0) {
            params["ordering"] = ordering;
        }
        const resp = await ax.get(url, {params: params});
        PlayServerClient.assertJson(resp);
        return RegReportResult.fromJson(resp.data);
    }

    async regReportCompanies(
        companyOrgNum: number,
        from: DateTime | null,
        to: DateTime | null,
        filterLocationIds: number[],
        filterUserGuids: string[],
    ): Promise<Company[]> {
        const url = Routes.regReportFilterCompanies(companyOrgNum);
        const params = {
            from: from != null ? from.toJSON() : null,
            to: to != null ? to.toJSON() : null,
        }
        if (filterLocationIds.length > 0) {
            params["filterLocationIds"] = filterLocationIds.join(",")
        }
        if (filterUserGuids.length > 0) {
            params["filterUserGuids"] = filterUserGuids.join(",")
        }
        const resp = await ax.get(url, {params: params});
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(r => Company.fromJson(r));
    }

    async regReportUsers(
        companyOrgNum: number,
        from: DateTime | null,
        to: DateTime | null,
        filterOrgnums: number[],
        filterLocationIds: number[],
        usrSearch: string | null,
    ): Promise<GuiUserlisting[]> {
        const url = Routes.regReportFilterUsers(companyOrgNum);
        const params = {
            from: from != null ? from.toJSON() : null,
            to: to != null ? to.toJSON() : null,
        }
        if (filterOrgnums.length > 0) {
            params["filterOrgnums"] = filterOrgnums.join(",");
        }
        if (filterLocationIds.length > 0) {
            params["filterLocationIds"] = filterLocationIds.join(",")
        }
        if (usrSearch != null) {
            params["usrSearch"] = usrSearch
        }
        const resp = await ax.get(url, {params: params});
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(r => GuiUserlisting.fromJson(r));
    }

    static contentType(resp: any) {
        if (typeof resp == "object" && typeof resp.headers == "object") {
            return resp.headers["content-type"];
        }
        return null;
    }

    static assertJson(resp: any) {
        let typ = this.contentType(resp);
        if ("application/json" != typ) {
            throw new InvalidResponseType("application/json", typ);
        }
    }

    static assertJsonArray(resp: any) {
        const contentType = this.contentType(resp);
        if (contentType != "application/json") {
            throw new InvalidResponseType("application/json", contentType);
        }
        if (!(resp.data instanceof Array)) {
            throw new InvalidResponseType("array", typeof resp.data);
        }
    }

    async fetchTerminalLocation(id: number): Promise<TerminalLocation> {
        const url = Routes.fetchTerminalLocation(id);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        return TerminalLocation.fromJson(resp.data);
    }

    async fetchHmsregInfo(locid: number): Promise<TerminalLocationHmsregInfo> {
        const url = Routes.fetchHmsregInfo(locid);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        return TerminalLocationHmsregInfo.fromJson(resp.data);
    }

    async fetchTerminalsActiveOnLocation(locid: number): Promise<Terminal[]> {
        const url = Routes.terminalsActiveOnLocation(locid);
        const resp = await ax.get(url);
        if (resp.data instanceof Array) {
            const arr = resp.data as any[];
            return arr.map((t) => Terminal.fromJson(t));
        } else {
            throw new InvalidResponseType("array", typeof resp.data);
        }
    }

    async fetchTerminalsPendingOnLocation(locid: number): Promise<TerminalConfigNext[]> {
        const url = Routes.terminalsPendingOnLocation(locid);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        const arr = resp.data as any[];
        return arr.map((t) => TerminalConfigNext.fromJson(t));
    }

    async fetchOnsiteCount(locid: number): Promise<number> {
        const url = Routes.onsiteCount(locid);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        return requireNum(resp.data);
    }

    async terminalLocationUpdate(terminalLocation: TerminalLocation): Promise<boolean> {
        const url = Routes.terminalLocationUpdate(terminalLocation.id);
        const resp = await ax.post(url, terminalLocation.toJson(), this.contentTypeJsonConfig());
        return true;
    }

    async hmsregLocationUpdate(locid: number, hmsregNumber: number | null): Promise<number | null> {
        const url = Routes.hmsregLocationUpdate(locid);
        const resp = await ax.post(url, {hmsregnumber: hmsregNumber});
        PlayServerClient.assertJson(resp);
        return numOrNull(resp.data.sioCount)
    }

    async hmsregSend(locid: number): Promise<number | null> {
        const url = Routes.hmsregSend(locid);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return numOrNull(resp.data.sioCount);
    }

    private contentTypeJsonConfig() {
        return {
            headers: {
                "content-type": "application/json"
            }
        }
    }

    async exportserviceLocationRefUpdate(locid: number, infos: TerminalLocationExportserviceInfo[]): Promise<number | null> {
        const url = Routes.exportserviceLocationRefUpdate(locid);
        const postdata = JSON.stringify(infos);
        const resp = await ax.post(url, postdata, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return numOrNull(resp.data.sioCount);
    }

    async exportserviceSioSend(locid: number, exportserviceHeaderId: number): Promise<number | null> {
        const url = Routes.exportserviceSioSend(locid);
        const resp = await ax.post(url, JSON.stringify({"exportservice-header-id": exportserviceHeaderId}), this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return numOrNull(resp.data.sioCount);
    }

    async fetchOnsiteLines(locid: number): Promise<SiteInOutReportLine[]> {
        const url = Routes.onsiteLines(locid);
        const resp = await ax.get(url, {
            headers: {
                "Accept": "application/json",
            }
        });
        if (resp.data instanceof Array) {
            const arr = resp.data as any[];
            return arr.map((r) => SiteInOutReportLine.fromJson(r));
        } else {
            throw new InvalidResponseType("array", typeof resp.data);
        }
    }

    async sioRegOutNow(guid: string, geolocation: JLocation | null): Promise<string | ApiMessage> {
        const url = Routes.sioRegOutNow(guid);
        const resp = await ax.post(url, {geolocation: geolocation}, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        if (PlayServerClient.hasResponseApiMessage(resp)) {
            return ApiMessage.fromJson(resp.data);
        }
        return requireStr(resp.data.undoTxguid);
    }

    async sioAdminRegOutNow(guid: string): Promise<string | ApiMessage> {
        const url = Routes.sioAdminRegOutNow(guid);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        if (PlayServerClient.hasResponseApiMessage(resp)) {
            return ApiMessage.fromJson(resp.data);
        }
        return requireStr(resp.data.undoTxguid);
    }

    async undoSioRegOut(guid: string, undoTxGuid: string): Promise<boolean> {
        const url = Routes.undoSioRegOut(guid, undoTxGuid);
        const resp = await ax.post(url)
        return resp.data === true;
    }

    async userList(filterTxt: string, pageNum: number): Promise<GuiUserlisting[]> {
        const url = Routes.userList(filterTxt, pageNum);
        const resp = await ax.get(url, {params: {"filterTxt": filterTxt, "pageNum": pageNum}});
        if (resp.data instanceof Array) {
            const arr = resp.data as any[];
            return arr.map((r) => GuiUserlisting.fromJson(r));
        } else {
            throw new InvalidResponseType("array", typeof resp.data);
        }
    }

    async userData(personRef: string): Promise<CardUserdata | null> {
        const url = Routes.userData(personRef);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        const data = CardUserdata.fromJson(resp.data);
        if (data != null) {
            data.roles = data.roles.filter((r: RolesForCompany) => r.companyRoles.length > 0 || r.locationRoles.length > 0);
        }
        return data;
    }

    async getLastIn(personRef: string): Promise<LastIn | null> {
        const url = Routes.getLastIn(personRef);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            return LastIn.fromJson(resp.data);
        }
        return null;
    }

    /**
     * Save edited person info to db.
     */
    async updateUvpInfo(guid: string, firstName: string | null, lastName: string | null, birthDate: DateTime | null, employedAt: Company | null): Promise<boolean> {
        const url = Routes.updateUvpInfo(guid);
        // custom request, birthDate is parsed as LocalDate on server endpoint
        const req = {
            "firstName": firstName,
            "lastName": lastName,
            "birthDate": birthDate != null ? birthDate.toLocalJsonDate() : null,
            "employedAt": employedAt,
        };
        const resp = await ax.post(url, req, this.contentTypeJsonConfig());
        return true;
    }

    async removeUserRole(orgnum: number, personRef: string, roleId: number, maybeLocationId: number | null): Promise<boolean> {
        const url = Routes.removeUserRole(orgnum, personRef, roleId, maybeLocationId);
        const resp = await ax.post(url);
        return true;
    }

    async fetchGrantableCompanies(): Promise<Company[]> {
        const url = Routes.fetchGrantableCompanies();
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => Company.fromJson(r));
    }

    async addUserRole(orgnum: number, personRef: string, roleid: number, locationId: number | null): Promise<boolean> {
        const url = Routes.addUserRole(orgnum, personRef);
        const postData = JSON.stringify({"orgnum": orgnum, "roleId": roleid, "locationId": locationId});
        const resp = await ax.post(url, postData, this.contentTypeJsonConfig());
        return true;
    }

    async mergePersons(fromGuid: string, toGuid: string): Promise<PersonMergeResult> {
        const url = Routes.mergePersons(fromGuid, toGuid);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return PersonMergeResult.fromJson(resp.data);
    }

    async undoPersonMerge(txid: number): Promise<boolean> {
        const url = Routes.undoPersonMerge(txid);
        const resp = await ax.post(url);
        return true;
    }

    async fetchTerminals(orgnum: number, showArchived: boolean): Promise<TerminalGuiInfo[]> {
        const url = Routes.fetchTerminals(orgnum);
        const resp = await ax.get(url, {params: {"showArchived": showArchived}});
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => TerminalGuiInfo.fromJson(r));
    }

    async fetchTerminal(guid: string): Promise<TerminalGuiInfo> {
        const url = Routes.fetchTerminal(guid);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        return TerminalGuiInfo.fromJson(resp.data);
    }

    async currentUser(): Promise<CardUserdata | null> {
        const url = Routes.currentUser();
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        return CardUserdata.fromJson(resp.data);
    }

    async terminalDescriptionSave(guid: string, description: string): Promise<boolean> {
        const url = Routes.terminalDescriptionSave(guid);
        const resp = await ax.post(url, {"description": description}, this.contentTypeJsonConfig());
        return true;
    }

    async terminalContinuousPushlistenerSet(guid: string, continuous: boolean): Promise<void> {
        const url = Routes.terminalContinuousPushlistenerSet(guid);
        await ax.post(url, {"continuousPushlistener": continuous}, this.contentTypeJsonConfig());
        return;
    }

    async fetchTerminalConfigNext(guid: string): Promise<TerminalConfigNext | null> {
        const url = Routes.fetchTerminalConfigNext(guid);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            return TerminalConfigNext.fromJson(resp.data);
        }
        return null;
    }

    async terminalActivate(guid: string): Promise<boolean> {
        const url = Routes.terminalActivate(guid);
        const resp = await ax.post(url);
        return true;
    }

    async terminalDeactivate(guid: string): Promise<TerminalLocation | null> {
        const url = Routes.terminalDeactivate(guid);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            return TerminalLocation.fromJson(resp.data)
        } else {
            return null;
        }
    }

    async terminalCleanupMark(guid: string): Promise<boolean> {
        const url = Routes.terminalCleanupMark(guid);
        const resp = await ax.post(url);
        return true;
    }

    async terminalCleanupUndo(guid: string): Promise<ApiMessage | null> {
        const url = Routes.terminalCleanupUndo(guid);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        if (PlayServerClient.hasResponseApiMessage(resp)) {
            return ApiMessage.fromJson(resp.data);
        }
        return null;
    }

    async terminalLocationSet(guid: string, locid: number): Promise<boolean> {
        const url = Routes.terminalLocationSet(guid);
        const resp = await ax.post(url, {"location-id": locid}, this.contentTypeJsonConfig());
        return true;
    }

    async terminalSyncPush(guid: string): Promise<boolean> {
        const url = Routes.terminalSyncPush(guid);
        const resp = await ax.post(url);
        return true;
    }

    async terminalConfigPush(guid: string): Promise<boolean> {
        const url = Routes.terminalConfigPush(guid);
        const resp = await ax.post(url);
        return true;
    }

    async addInvoicingExceptedTerminal(terminalGuid: string, from: DateTime, to: DateTime, reason: string): Promise<boolean> {
        const url = Routes.addInvoicingExceptedTerminal();
        const data = {
            "terminal-ref": terminalGuid,
            "from": from.toJSON(),
            "to": to.toJSON(),
            "reason": reason
        };
        const resp = await ax.post(url, data, this.contentTypeJsonConfig());
        return true;
    }

    async addInvoicingExceptedLocation(locid: number, from: DateTime, to: DateTime, reason: string): Promise<boolean> {
        const url = Routes.addInvoicingExceptedLocation();
        const data = {
            "locid": locid,
            "from": from.toJSON(),
            "to": to.toJSON(),
            "reason": reason
        };
        const resp = await ax.post(url, data, this.contentTypeJsonConfig());
        return true;
    }

    async delInvoicingExceptedTerminal(id: number): Promise<boolean> {
        const url = Routes.delInvoicingExceptedTerminal();
        const data = {"expected-id": id};
        await ax.post(url, data, this.contentTypeJsonConfig());
        return true;
    }

    async delInvoicingExceptedLocation(id: number): Promise<boolean> {
        const url = Routes.delInvoicingExceptedLocation();
        const data = {"excepted-id": id};
        await ax.post(url, data, this.contentTypeJsonConfig());
        return true;
    }

    async fetchInvoicingExceptedTerminals(terminalGuid: string): Promise<InvoicingExceptedTerminal[]> {
        const url = Routes.fetchInvoicingExceptedTerminals(terminalGuid);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => InvoicingExceptedTerminal.fromJson(r));
    }

    async fetchInvoicingExceptedLocations(locid: number): Promise<InvoicingExceptedLocation[]> {
        const url = Routes.fetchInvoicingExceptedLocations(locid);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => InvoicingExceptedLocation.fromJson(r));
    }

    async terminalAlarmStatus(terminalGuid: string): Promise<number> {
        const url = Routes.terminalAlarmstatus(terminalGuid);
        const resp = await ax.get(url);
        return requireNum(resp.data);
    }

    async terminalGeolocation(terminalGuid: string): Promise<JLocation | null> {
        const url = Routes.terminalGeolocation(terminalGuid);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data && resp.data.latitude) {
            return JLocation.fromJson(resp.data);
        }
        return null;
    }

    async setDisconnectAlarmFromServer(terminalGuid: string, newState: number): Promise<boolean> {
        const url = Routes.setDisconnectAlarmFromServer(terminalGuid, newState);
        await ax.post(url);
        return true;
    }

    async fetchCustomerRef(orgnum: number): Promise<number | null> {
        const url = Routes.fetchCustomerRef(orgnum);
        const resp = await ax.get(url);
        return numOrNull(resp.data);
    }

    async fetchRoleCompanies(): Promise<Company[]> {
        const url = Routes.fetchRoleCompanies();
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => Company.fromJson(r));
    }

    async fetchUsermessages(archived: boolean, page: number): Promise<UsrMsg[]> {
        const url = Routes.fetchUsermessages();
        const resp = await ax.get(url, {params: {"archived": archived, "page": page}});
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => UsrMsg.fromJson(r));
    }

    async unreadUsermessagesCount(): Promise<number> {
        const url = Routes.unreadUsermessagesCount();
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        return resp.data;
    }

    async archiveUsermessage(guid: string): Promise<void> {
        const url = Routes.archiveUsermessage(guid);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return;
    }

    async setUsermessageRead(guid: string): Promise<void> {
        const url = Routes.setUsermessageRead(guid);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return;
    }

    async fetchExportserviceHeaderRows(orgnum: number): Promise<ExportserviceHeaderRow[]> {
        const url = Routes.fetchExportserviceHeaderRows(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => ExportserviceHeaderRow.fromJson(r));
    }

    async fetchHmsregIntegrationCompany(orgnum: number): Promise<HmsregIntegrationCompany | null> {
        const url = Routes.fetchHmsregIntegrationCompany(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data != "" && resp.data.companyOrgnum) {
            return HmsregIntegrationCompany.fromJson(resp.data);
        } else {
            return null;
        }
    }

    async fetchHmsregIntegrationLocCount(orgnum: number): Promise<number> {
        const url = Routes.fetchHmsregIntegrationLocCount(orgnum);
        const resp = await ax.get(url);
        return maybeNum(resp.data);
    }

    async fetchHmsregLocationStatuses(orgnum: number): Promise<HmsregLocationStatus[]> {
        const url = Routes.fetchHmsregLocationStatuses(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(r => HmsregLocationStatus.fromJson(r));
    }

    async fetchHmsregLocationStatus(locid: number): Promise<HmsregLocationStatus | null> {
        const url = Routes.fetchHmsregLocationStatus(locid);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            return HmsregLocationStatus.fromJson(resp.data);
        }
        return null;
    }

    async hmsregIntegrationSettingsActivate(setting: HmsregIntegrationCompany): Promise<boolean> {
        const url = Routes.hmsregIntegrationSettingsActivate(setting.companyOrgnum);
        const resp = await ax.post(url, setting, this.contentTypeJsonConfig());
        return true;
    }

    async hmsregIntegrationSettingsSave(setting: HmsregIntegrationCompany): Promise<void> {
        const url = Routes.hmsregIntegrationSettingsSave(setting.companyOrgnum);
        await ax.post(url, setting, this.contentTypeJsonConfig());
        return;
    }

    async hmsregIntegrationSettingsDeactivate(orgnum: number): Promise<boolean> {
        const url = Routes.hmsregIntegrationSettingsDeactivate(orgnum);
        await ax.post(url);
        return true;
    }

    async autoTmcCompanySettings(orgnum: number): Promise<ProresultIntegrationCompanySettings | null> {
        const url = Routes.autoTmcCompanySettings(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data != "") {
            return ProresultIntegrationCompanySettings.fromJson(resp.data);
        } else {
            return null;
        }
    }

    async autoTmcFailedCount(orgnum: number): Promise<number | null> {
        const url = Routes.autoTmcFailedCount(orgnum);
        const resp = await ax.get(url);
        return numOrNull(resp.data);
    }

    async exportserviceSettingsCRUD(orgnum: number, op: string, id: number, description: string | null, url: string | null, username: string | null, secret: string | null, authMethod: string): Promise<string> {
        const urlReq = Routes.exportserviceSettingsCRUD(orgnum);
        const data = {
            "op": op,
            "id": id,
            "url": url,
            "username": username,
            "secret": secret,
            "description": description,
            "authMethod": authMethod,
        };
        const resp = await ax.post(urlReq, data, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return resp.data.message;
    }

    // for super only
    async fetchAllCompanies(): Promise<Company[]> {
        const url = Routes.fetchAllCompanies();
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => Company.fromJson(r));
    }

    async addSuperRole(orgnum: number): Promise<boolean> {
        const url = Routes.addSuperRole(orgnum);
        await ax.post(url);
        return true;
    }

    async saveCustomerRef(orgnum: number, customerRef: number): Promise<boolean> {
        const url = Routes.saveCustomerRef(orgnum);
        const data = {
            "customer-ref": customerRef
        };
        await ax.post(url, data, this.contentTypeJsonConfig());
        return true;
    }

    async exportserviceDelete(id: number): Promise<boolean> {
        const url = Routes.exportserviceDelete();
        await ax.post(url, {"id": id}, this.contentTypeJsonConfig());
        return true;
    }

    async addCompany(orgnum: number): Promise<string> {
        const url = Routes.addCompany();
        const data = {
            "orgnum": orgnum,
        };
        const resp = await ax.post(url, data, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return resp.data.message;
    }

    async triggerLogSend(terminalGuid: string): Promise<boolean> {
        const url = Routes.triggerLogsend(terminalGuid);
        await ax.post(url);
        return true;
    }

    async cleanupDevice(terminalGuid: string): Promise<string> {
        const url = Routes.cleanupDevice(terminalGuid);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return resp.data.message;
    }

    async reinitDevice(terminalGuid: string, deviceid: string | null): Promise<string> {
        const url = Routes.reinitDevice(terminalGuid);
        const data = deviceid ? {deviceid: deviceid} : {};
        const resp = await ax.post(url, data, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return resp.data.message;
    }

    async fetchPendingConfigs(orgnum: number): Promise<TerminalConfigNext[]> {
        const url = Routes.fetchPendingConfigs(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => TerminalConfigNext.fromJson(r));
    }

    async deletePendingconfig(terminalGuid: string): Promise<boolean> {
        const url = Routes.deletePendingconfig(terminalGuid);
        await ax.post(url);
        return true;
    }

    async newTerminalSetup(orgnum: number, deviceid: string, description: string, locid: number | null): Promise<ApiMessage | null> {
        const url = Routes.newTerminalSetup();
        const data = {
            "orgnum": orgnum,
            "deviceid": deviceid,
            "description": description,
            "locid": locid
        };
        const resp = await ax.post(url, data, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        if (PlayServerClient.hasResponseApiMessage(resp)) {
            return ApiMessage.fromJson(resp.data);
        }
        return null;
    }

    async companyMigrate(fromOrgnum: number, toOrgnum: number): Promise<string> {
        const url = Routes.companyMigratePost();
        const data = {
            "from-orgnum": fromOrgnum,
            "to-orgnum": toOrgnum
        };
        const resp = await ax.post(url, data, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return resp.data.message;
    }

    protected async doLogin(phone: string, password: string): Promise<string> {
        const url = Routes.loginPostJson();
        const data = {
            "phone": phone,
            "password": password
        };
        const resp = await ax.post(url, data, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return requireStr(resp.data);
    }

    async loginRenew(phone: string): Promise<string> {
        const url = Routes.loginRenew();
        const resp = await ax.post(url, {"phone": phone}, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return resp.data.message;
    }

    async fetchPhonelistReport(orgnum: number): Promise<PhonelistReportRow[]> {
        const url = Routes.fetchPhonelistReport(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => PhonelistReportRow.fromJson(r));
    }

    async fetchAllUsersReport(orgnum: number): Promise<AllUsersReportRow[]> {
        const url = Routes.fetchAllUsersReport(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => AllUsersReportRow.fromJson(r));
    }

    async fetchUserTermlocs(): Promise<TerminalLocation[]> {
        const url = Routes.fetchUserTermlocs();
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => TerminalLocation.fromJson(r));
    }

    async fetchUserSiteReport(locid: number): Promise<UserSiteReportRow[]> {
        const url = Routes.fetchUserSiteReport(locid);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => UserSiteReportRow.fromJson(r));
    }

    async removeSelfPhone(phone: PhoneNumber): Promise<number> {
        const url = Routes.removeSelfPhone(phone);
        const resp = await ax.delete(url);
        PlayServerClient.assertJson(resp);
        return requireNum(resp.data.txid);
    }

    async undoRemoveSelfPhone(txid: number): Promise<void> {
        const url = Routes.undoRemoveSelfPhone(txid);
        await ax.post(url);
        return;
    }

    private phoneCountryCodesPromise: Promise<CountryCode[]> | null = null;

    async fetchPhoneCountryCodes(): Promise<CountryCode[]> {
        if (this.phoneCountryCodesPromise == null) {
            const url = Routes.fetchPhoneCountryCodes();
            this.phoneCountryCodesPromise = ax.get(url).then((resp) => {
                PlayServerClient.assertJsonArray(resp);
                return (resp.data as any[]).map((r) => CountryCode.fromJson(r));
            });
        }
        return await this.phoneCountryCodesPromise;
    }

    async addPersonPhone(personRef: string, phone: PhoneNumber): Promise<ApiMessage> {
        const url = Routes.addPersonPhone(personRef);
        const resp = await ax.post(url, phone, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async removePendingPhone(personRef: string, phone: PhoneNumber): Promise<void> {
        const url = Routes.removePendingPhone(personRef);
        await ax.post(url, phone, this.contentTypeJsonConfig());
        return;
    }

    async confirmPendingPhone(phone: PhoneNumber, secret: number): Promise<ApiMessage> {
        const url = Routes.confirmPendingPhone();
        const data = {
            phone: phone,
            secret: secret
        };
        const resp = await ax.post(url, data, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async resendPendingPhoneSecret(personRef: string, phone: PhoneNumber): Promise<ApiMessage> {
        const url = Routes.resendPendingPhoneSecret(personRef);
        const resp = await ax.post(url, phone, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async checkSessionLoginKey(key: string): Promise<ApiMessage> {
        const url = Routes.checkSessionLoginKey(key);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async confirmLoginPKey(key: string, termsAccepted: boolean = false): Promise<ApiMessage> {
        const url = Routes.confirmLoginPKey(key);
        const resp = await ax.post(url, {termsAccepted: termsAccepted}, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async pkeyLogin(key: string): Promise<ApiMessage> {
        const url = Routes.loginLoginPKey(key);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async sessionPhone(): Promise<PhoneNumber | null> {
        const url = Routes.sessionPhone();
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            return PhoneNumber.fromJson(resp.data);
        }
        return null;
    }

    async brregSearch(txt: string): Promise<Company[]> {
        const url = Routes.brregSearch(txt);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => Company.fromJson(r));
    }

    private async internalPendingPersonAdd(pendingPerson: PendingPerson, url: string): Promise<ApiMessage> {
        const resp = await ax.post(url, pendingPerson, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async pendingPersonAdd(pendingPerson: PendingPerson): Promise<ApiMessage> {
        return await this.internalPendingPersonAdd(pendingPerson, Routes.pendingPersonAdd())
    }

    async pendingPersonAddPrelogin(pendingPerson: PendingPerson): Promise<ApiMessage> {
        return await this.internalPendingPersonAdd(pendingPerson, Routes.pendingPersonAddPrelogin())
    }

    async pendingPersonsList(): Promise<PendingPerson[]> {
        const url = Routes.pendingPersonsList();
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(r => PendingPerson.fromJson(r));
    }

    async pendingPersonDelete(guid: string): Promise<number> {
        const url = Routes.pendingPersonDelete(guid);
        const resp = await ax.delete(url);
        PlayServerClient.assertJson(resp);
        return resp.data;
    }

    async pendingPhoneCheck(phoneStr: string): Promise<number> {
        const url = Routes.pendingPhoneCheck(phoneStr);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        return requireNum(resp.data);
    }

    async fetchPendingPhoneconfirm(key: string): Promise<PendingPhoneconfirm | null> {
        const url = Routes.fetchPendingPhoneconfirm(key);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            return PendingPhoneconfirm.fromJson(resp.data);
        } else {
            return null;
        }
    }

    async getLoginPkey(phone: PhoneNumber, secret: number): Promise<ApiMessage> {
        const url = Routes.getLoginPkey();
        const resp = await ax.post(url, {phone: phone, secret: secret}, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async getLoginCkey(phone: PhoneNumber, secret: number): Promise<ApiMessage> {
        const url = Routes.getLoginCkey();
        const resp = await ax.post(url, {phone: phone, secret: secret}, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async setDefaultLoginCompany(orgnum: number): Promise<void> {
        const url = Routes.setDefaultLoginCompany(orgnum);
        await ax.post(url);
        return;
    }

    async fetchPendingCardConfirm(ckey: string): Promise<PendingCardConfirm | ApiMessage | null> {
        const url = Routes.fetchPendingCardConfirm(ckey);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            if (PlayServerClient.hasResponseApiMessage(resp)) {
                return ApiMessage.fromJson(resp.data);
            } else {
                return PendingCardConfirm.fromJson(resp.data);
            }
        } else {
            return null;
        }
    }

    async confirmPendingCardPhone(ckey: string, merge: boolean): Promise<ApiMessage> {
        const url = Routes.confirmPendingCardPhone();
        const resp = await ax.post(url, {ckey: ckey, merge: merge}, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async ckeyLogin(key: string): Promise<ApiMessage> {
        const url = Routes.loginLoginCKey(key);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async activePersonSio(): Promise<ActiveSio | null> {
        const url = Routes.activePersonSio();
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            return ActiveSio.fromJson(resp.data);
        }
        return null;
    }

    async fetchTerminalLocationAreas(locid: number): Promise<TerminalLocationArea[]> {
        const url = Routes.terminalLocationAreas(locid);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        return (resp.data as any[]).map((json) => TerminalLocationArea.fromJson(json));
    }

    async saveTerminalLocationAreas(locid: number, tlocAreas: TerminalLocationArea[]): Promise<void> {
        const url = Routes.terminalLocationAreas(locid);
        await ax.post(url, tlocAreas, this.contentTypeJsonConfig());
        return
    }

    async fetchTerminalLocationGeofences(locid: number): Promise<TerminalLocationGeofence[]> {
        const url = Routes.terminalLocationGeofences(locid);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(json => TerminalLocationGeofence.fromJson(json))
    }

    async fetchAvailableTerminalLocations(position: GeoPoint): Promise<InOutAvailableLocation[]> {
        const url = Routes.fetchAvailableTerminalLocations();
        const resp = await ax.get(url, {params: {latitude: position.latitude, longitude: position.longitude}});
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(InOutAvailableLocation.fromJson);
    }

    async sioRegIn(locationId: number, geolocation: JLocation, cardNum: number | null): Promise<ApiMessage | null> {
        const url = Routes.sioRegIn();
        const data = {locationId: locationId, geolocation: geolocation, cardNum: cardNum};
        const resp = await ax.post(url, data, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            if (PlayServerClient.hasResponseApiMessage(resp)) {
                return ApiMessage.fromJson(resp.data);
            } else {
                return null;
            }
        } else {
            throw new Error("sioRegIn returned no response.");
        }
    }

    async personConfig(): Promise<PersonConfig> {
        const url = Routes.personConfig();
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        return PersonConfig.fromJson(resp.data);
    }

    private async simReportHelper(url: string): Promise<SimAndTerminalReportInfo[]> {
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => SimAndTerminalReportInfo.fromJson(r));
    }

    async simReportStatusOverdue(): Promise<SimAndTerminalReportInfo[]> {
        return await this.simReportHelper(Routes.simReportStatusOverdue());
    }

    async com4ReconciliationReport(): Promise<Com4ReconciliationReport> {
        const resp = await ax.get(Routes.com4ReconciliationReport());
        return Com4ReconciliationReport.fromJson(resp.data);
    }

    async com4ManualHistory(): Promise<Com4ManualHistoryEntry[]> {
        const resp = await ax.get(Routes.com4ManualHistory());
        PlayServerClient.assertJsonArray(resp);
        return resp.data;
    }

    async com4OperationEvents(operationId: string): Promise<Com4OperationEvent[]> {
        const resp = await ax.get(Routes.com4OperationEvents(operationId));
        PlayServerClient.assertJsonArray(resp);
        return resp.data;
    }

    async terminalsWithoutSIM(): Promise<TerminalWithoutSIM[]> {
        const resp = await ax.get(Routes.terminalsWithoutSIM());
        PlayServerClient.assertJsonArray(resp);
        return resp.data;
    }

    async linkTerminalSIM(terminalGuid: string, simSerial: string, confirmed: boolean): Promise<string> {
        const resp = await ax.post(Routes.linkTerminalSIM(), {terminalGuid, simSerial, confirmed}, this.contentTypeJsonConfig());
        return resp.data.message;
    }

    async requestCom4Refresh(): Promise<number> {
        const resp = await ax.post(Routes.com4Refresh(), {}, this.contentTypeJsonConfig());
        return resp.data.runId;
    }

    async com4RefreshStatus(): Promise<Com4InventoryRunReport | null> {
        const resp = await ax.get(Routes.com4Refresh());
        return resp.data && resp.data.runId != null ? Com4InventoryRunReport.fromJson(resp.data) : null;
    }

    async setCom4ManualSIMs(simSerials: string[], expectedControlId: number, reason: string): Promise<void> {
        await ax.post(Routes.com4ManualSIMs(), {
            simSerials,
            expectedControlId,
            reason,
        }, this.contentTypeJsonConfig());
    }

    async requestCom4ManualOperation(simSerial: string, targetStatus: string, expectedControlId: number): Promise<string> {
        const resp = await ax.post(Routes.com4ManualOperation(), {
            simSerial, targetStatus, expectedControlId,
        }, this.contentTypeJsonConfig());
        return resp.data.operationId;
    }

    async setCom4UsageAllowance(simSerial: string, month: string, limitMB: number | null, expectedId: number, reason: string): Promise<void> {
        await ax.post(Routes.com4UsageAllowance(), {simSerial, month, limitMB, expectedId, reason}, this.contentTypeJsonConfig());
    }

    async fetchSimRow(simSerial: string): Promise<SimRow | null> {
        const url = Routes.fetchSimRow(simSerial);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        return resp.data ? SimRow.fromJson(resp.data) : null;
    }

    async fetchUserRoleReport(orgnum: number): Promise<UserRoleReportRow[]> {
        const url = Routes.fetchUserRoleReport(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => UserRoleReportRow.fromJson(r));
    }

    async fetchPendingTerminalSetups(orgnum: number): Promise<TerminalConfigNext[]> {
        const url = Routes.fetchPendingTerminalSetups(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => TerminalConfigNext.fromJson(r));
    }

    async pendingPersonRoleAdd(orgnum: number, personGuid: string, roleId: number, locationId: number | null): Promise<void> {
        const url = Routes.pendingPersonRoleAdd(orgnum);
        const data = {personGuid: personGuid, roleId: roleId, locId: locationId};
        await ax.post(url, data, this.contentTypeJsonConfig());
        return;
    }

    async pendingPersonRoleRemove(orgnum: number, personGuid: string, roleId: number, locationId: number | null): Promise<void> {
        const url = Routes.pendingPersonRoleRemove(orgnum);
        const data = {personGuid: personGuid, roleId: roleId, locId: locationId};
        await ax.post(url, data, this.contentTypeJsonConfig());
        return;
    }

    async terminalLocationShutdown(locid: number): Promise<ApiMessage> {
        const url = Routes.terminalLocationShutdown(locid);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async terminalLocationActivate(locid: number): Promise<ApiMessage> {
        const url = Routes.terminalLocationActivate(locid);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async cardQrInit(qrCodeUrl: string): Promise<number | ApiMessage> {
        const url = Routes.cardQrInit();
        const resp = await ax.post(url, {url: qrCodeUrl});
        PlayServerClient.assertJson(resp);
        if (PlayServerClient.hasResponseApiMessage(resp)) {
            return ApiMessage.fromJson(resp.data);
        } else {
            return requireNum(resp.data);
        }
    }

    async pendingCard(cardNum: number): Promise<PendingCardReply | ApiMessage> {
        const url = Routes.pendingCard(cardNum);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (PlayServerClient.hasResponseApiMessage(resp)) {
            return ApiMessage.fromJson(resp.data);
        } else {
            return PendingCardReply.fromJson(resp.data);
        }
    }

    async dismissCardAdd(): Promise<void> {
        const url = Routes.dismissCardAdd();
        await ax.post(url);
        return;
    }

    async pendingCardAdd(cardNum: number): Promise<void> {
        const url = Routes.pendingCardAdd(cardNum);
        await ax.post(url);
        return;
    }

    async pendingCardMerge(cardNum: number): Promise<void> {
        const url = Routes.pendingCardMerge(cardNum);
        await ax.post(url);
        return;
    }

    async pendingCardTakeover(cardNum: number): Promise<void> {
        const url = Routes.pendingCardTakeover(cardNum);
        await ax.post(url);
        return;
    }

    async fetchPersonmergeUndoInfo(personRef: string): Promise<PersonmergeUndoInfo | null> {
        const url = Routes.fetchPersonmergeUndoInfo();
        const resp = await ax.get(url, {params: {personRef: personRef}});
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            return PersonmergeUndoInfo.fromJson(resp.data);
        }
        return null;
    }

    async checkPhoneRegistration(phone: PhoneNumber): Promise<PhoneCheckReplies | ApiMessage> {
        const url = Routes.checkPhoneRegistration();
        const resp = await ax.get(url, {params: {countryCode: phone.countrycode, number: phone.number}});
        PlayServerClient.assertJson(resp);
        if (PlayServerClient.hasResponseApiMessage(resp)) {
            return ApiMessage.fromJson(resp.data);
        }
        return PhoneCheckReplies.fromJson(resp.data);
    }

    async qrConnectAvailable(): Promise<boolean> {
        const url = Routes.qrConnectAvailable();
        const resp = await ax.get(url);
        return resp.data === true;
    }

    async pendingCardPersonAdd(phone: PhoneNumber, cardNum: number): Promise<ApiMessage> {
        const url = Routes.pendingCardPersonAdd(phone);
        const resp = await ax.post(url, cardNum);
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async pendingCardPhoneConnect(phone: PhoneNumber, cardNum: number): Promise<ApiMessage> {
        const url = Routes.pendingCardPhoneConnect(phone);
        const resp = await ax.post(url, cardNum);
        PlayServerClient.assertJson(resp);
        return ApiMessage.fromJson(resp.data);
    }

    async activeBillingLocationsInfo(orgnum: number): Promise<BillingInfoLocation[]> {
        const url = Routes.activeBillingLocationsInfo(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => BillingInfoLocation.fromJson(r));
    }

    async activeBillingTerminalsInfo(orgnum: number): Promise<BillingInfoTerminal[]> {
        const url = Routes.activeBillingTerminalsInfo(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => BillingInfoTerminal.fromJson(r));
    }

    async imeiSearch(searchTxt: string): Promise<TerminalGuiInfo[]> {
        const imei = searchTxt.replace(/[^0-9]/g, "");
        if (imei.length < 4) {
            throw new Error(`imei search text must be only numbers, and more than three digits. Was: "${searchTxt}"`);
        }
        const url = Routes.imeiSearch(searchTxt);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => TerminalGuiInfo.fromJson(r));
    }

    async getTerminalProresultIntegrationSetting(guid: string): Promise<number> {
        const url = Routes.getTerminalProresultIntegrationSetting(guid);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        return requireNum(resp.data);
    }

    protected async doLogout(): Promise<void> {
        const url = Routes.logout();
        await ax.post(url);
        return;
    }

    async fetchTerminalLocationRegcodes(locid: number): Promise<TerminalLocationRegcode[]> {
        const url = Routes.fetchTerminalLocationRegcodes(locid);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => TerminalLocationRegcode.fromJson(r));
    }

    async createTerminalLocationRegcode(locid: number): Promise<TerminalLocationRegcode[] | ApiMessage> {
        const url = Routes.createTerminalLocationRegcode(locid);
        const resp = await ax.post(url);
        if (PlayServerClient.hasResponseApiMessage(resp)) {
            return ApiMessage.fromJson(resp.data);
        }
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => TerminalLocationRegcode.fromJson(r));
    }

    async setTerminalLocationRegcodeState(regcodeid: number, enable: boolean): Promise<TerminalLocationRegcode[]> {
        const url = Routes.setTerminalLocationRegcodeState(regcodeid);
        const resp = await ax.post(url, {enable: enable});
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map((r) => TerminalLocationRegcode.fromJson(r));
    }

    async registerRegcode(regcode: string): Promise<RegcodeCheckReply> {
        const url = Routes.registerRegcode();
        const resp = await ax.post(url, {regcode: regcode});
        PlayServerClient.assertJson(resp);
        return RegcodeCheckReply.fromJson(resp.data);
    }

    async forgetRegcode(locid: number): Promise<number> {
        const url = Routes.forgetRegcode(locid)
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp)
        return requireNum(resp.data);
    }

    // Get a new auth token from the server
    async newMobileServerAuthtoken(): Promise<string> {
        const url = Routes.newMobileServerAuthtoken();
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return requireStr(resp.data);
    }

    async checkCompanyInitBlocked(orgnum: number): Promise<null | ApiMessage> {
        const url = Routes.checkCompanyInitBlocked(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            return ApiMessage.fromJson(resp.data);
        }
        return null;
    }

    async getBrregCompany(orgnum: number): Promise<Company | null> {
        const url = Routes.getBrregCompany(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            return Company.fromJson(resp.data);
        }
        return null;
    }

    async saveCompanyAccount(account: CompanyAccount): Promise<void> {
        const url = Routes.saveCompanyAccount();
        const resp = await ax.post(url, account);
        PlayServerClient.assertJson(resp);
        return;
    }

    async getCompanyAccount(orgnum: number): Promise<CompanyAccount | null> {
        const url = Routes.getCompanyAccount(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            return CompanyAccount.fromJson(resp.data);
        }
        return null;
    }

    async getCompanyAccountQuotas(orgnum: number): Promise<AccountQuotas> {
        const url = Routes.getCompanyAccountQuotas(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        return AccountQuotas.fromJson(resp.data);
    }

    async saveCompanyAccountQuotas(orgnum: number, quotas: AccountQuotas) {
        const url = Routes.saveCompanyAccountQuotas(orgnum);
        const resp = await ax.post(url, quotas);
        PlayServerClient.assertJson(resp);
        return;
    }

    async savePurchaseOrder(order: PurchaseOrder): Promise<number> {
        const url = Routes.savePurchaseOrder();
        const resp = await ax.post(url, order, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return requireNum(resp.data);
    }

    async getPurchaseOrder(id: number): Promise<PurchaseOrder | null> {
        const url = Routes.getPurchaseOrder(id);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (resp.data) {
            return PurchaseOrder.fromJson(resp.data);
        }
        return null;
    }

    async listPurchaseOrders(orgnum: number): Promise<PurchaseOrder[]> {
        const url = Routes.listPurchaseOrders();
        const resp = await ax.get(url, {params: {orgnum: orgnum}});
        PlayServerClient.assertJsonArray(resp);
        const ret = (resp.data as any[]).map(v => PurchaseOrder.fromJson(v));
        return ret;
    }

    async deletePurchaseOrder(id: number): Promise<void> {
        const url = Routes.deletePurchaseOrder(id);
        const resp = await ax.delete(url);
        PlayServerClient.assertJson(resp);
        return;
    }

    async increaseCompanyInvoiceException(orgnum: number): Promise<void> {
        const url = Routes.increaseCompanyInvoiceException(orgnum);
        await ax.post(url);
        return;
    }

    async reduceCompanyInvoiceException(orgnum: number): Promise<void> {
        const url = Routes.reduceCompanyInvoiceException(orgnum);
        await ax.post(url);
        return;
    }

    async fetchUserRegreminderLocations(): Promise<PersonRegreminderLocation[]> {
        const url = Routes.fetchUserRegreminderLocations();
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(r => PersonRegreminderLocation.fromJson(r));
    }

    async setRegreminder(locationId: number, enabled: boolean): Promise<SetRegreminderResponse> {
        const url = Routes.setRegreminder(locationId, enabled);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return SetRegreminderResponse.fromJson(resp.data);
    }

    async monthCountStat(url: string): Promise<MonthCountStat[]> {
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(r => MonthCountStat.fromJson(r));
    }

    async fetchCardExpirationReportCompanyLocations(orgnum: number, maxExpired: DateTime, minTimeIn: DateTime): Promise<CardExpirationReportRow[]> {
        const url = Routes.fetchCardExpirationReportLocations(orgnum);
        const params = {
            maxExpired: maxExpired.toDayParam(),
            minTimeIn: minTimeIn.toDayParam(),
        };
        const resp = await ax.get(url, {params: params});
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(r => CardExpirationReportRow.fromJson(r))
    }

    async fetchCardExpirationReportCompanyCards(orgnum: number, maxExpired: DateTime, minTimeIn: DateTime): Promise<CardExpirationReportRow[]> {
        const url = Routes.fetchCardExpirationReportCompanyCards(orgnum);
        const params = {
            maxExpired: maxExpired.toDayParam(),
            minTimeIn: minTimeIn.toDayParam(),
        };
        const resp = await ax.get(url, {params: params});
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(r => CardExpirationReportRow.fromJson(r))
    }

    async fetchUsersWithoutCardReport(orgnum: number): Promise<UsersWithoutCardReportRow[]> {
        const url = Routes.fetchUsersWithoutCardReport(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(r => UsersWithoutCardReportRow.fromJson(r));
    }

    /* ============= from RealApi, methods that just has a different name. Callers should be rewritten to use the name these refer to ============== */
    createNewTerminalLocation(loc: TerminalLocation, hmsregInfo: TerminalLocationHmsregInfo, exportserviceInfoList: TerminalLocationExportserviceInfo[]): Promise<ApiMessage | TerminalLocationWithStats> {
        return this.terminalLocationCreate(loc, hmsregInfo, exportserviceInfoList);
    }

    hmsregIsCompanyActivated(orgnum: number): Promise<boolean> {
        return this.isHmsregActivated(orgnum);
    }

    companyExportserviceInfoList(orgnum: number): Promise<TerminalLocationExportserviceInfo[]> {
        return this.exportserviceInfoList(orgnum, null);
    }

    locationExportserviceInfoList(orgnum: number, locid: number): Promise<TerminalLocationExportserviceInfo[]> {
        return this.exportserviceInfoList(orgnum, locid);
    }

    updateTerminalLocation(loc: TerminalLocation): Promise<boolean> {
        return this.terminalLocationUpdate(loc);
    }

    sendSiosToHmsreg(locid: number): Promise<number | null> {
        return this.hmsregSend(locid);
    }

    saveExportserviceInfos(locid: number, exportserviceInfos: TerminalLocationExportserviceInfo[]): Promise<number | null> {
        return this.exportserviceLocationRefUpdate(locid, exportserviceInfos);
    }

    sendExportserviceSios(locationId: number, exportserviceHeaderId: number): Promise<number | null> {
        return this.exportserviceSioSend(locationId, exportserviceHeaderId);
    }

    async fetchExportserviceInfos(orgnum: number, locid: number): Promise<TerminalLocationExportserviceInfo[]> {
        return this.locationExportserviceInfoList(orgnum, locid);
    }

    async loadReportData(orgnum: number, locid: number, from: DateTime, to: DateTime, filterOrgNum: number): Promise<SiteInOutReportLine[]> {
        return this.siteReport(orgnum, locid, from, to, filterOrgNum);
    }

    async saveUserEdit(guid: string, firstName: string | null, lastName: string | null, birthDate: DateTime | null, employedAt: Company | null): Promise<boolean> {
        return this.updateUvpInfo(guid, firstName, lastName, birthDate, employedAt);
    }

    async fetchGrantableLocations(orgnum: number): Promise<TerminalLocation[]> {
        const withStats = await this.fetchLocations(orgnum);
        return withStats.map((ws) => ws.termloc);
    }

    async login(phone: string, password: string, nativeApi: NativeApi): Promise<boolean> {
        const personRef = await this.doLogin(phone, password);
        if (nativeApi.available && nativeApi.atleastVersion(0, 2, 0)) { // Propagate user id (personRef) and mobile service auth token to app
            const token = await this.newMobileServerAuthtoken();
            await nativeApi.setRpcAuthtoken(token, personRef);
        }
        return true;
    }

    async logout(nativeApi: NativeApi): Promise<void> {
        await this.doLogout();
        if (nativeApi.available && nativeApi.atleastVersion(0, 2, 0)) { // Remove mobile service auth token in app
            await nativeApi.setRpcAuthtoken("", "");
        }
    }

    /* ============= end of RealApi methods ========= */


    async getFamocoDeviceInfo(terminalGuid: string): Promise<FamocoDevice | null> {
        const url = Routes.getFamocoDeviceInfo(terminalGuid);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        return resp.data ? FamocoDevice.fromJson(resp.data) : null;
    }

    async refreshFamocoDeviceInfo(deviceId: number): Promise<void> {
        const url = Routes.refreshFamocoDeviceInfo(deviceId);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return;
    }

    async addPersonEmail(personRef: string, email: string): Promise<ApiMessage> {
        const url = Routes.addPersonEmail(personRef)
        const resp = await ax.post(url, `"${email}"`, this.contentTypeJsonConfig())
        PlayServerClient.assertJson(resp)
        return ApiMessage.fromJson(resp.data)
    }

    async removePendingPersonEmail(id: number): Promise<void> {
        const url = Routes.removePendingPersonEmail(id);
        await ax.post(url);
        return;
    }

    async removePersonEmail(id: number): Promise<void> {
        const url = Routes.removePersonEmail(id);
        await ax.post(url);
        return;
    }

    async undoRemovePersonEmail(id: number): Promise<boolean> {
        const url = Routes.undoRemovePersonEmail(id);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return resp.data === true;
    }

    async fetchPendingEmailConfirmInfo(secret: string): Promise<PendingEmailConfirmInfo | ApiMessage> {
        const url = Routes.fetchPendingEmailConfirmInfo(secret);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        if (PlayServerClient.hasResponseApiMessage(resp)) {
            return ApiMessage.fromJson(resp.data);
        }
        return PendingEmailConfirmInfo.fromJson(resp.data);
    }

    async confirmPendingPersonEmail(secret: string): Promise<boolean> {
        const url = Routes.confirmPendingEmailConfirmInfo(secret)
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp)
        return resp.data === true;
    }

    async addNotificationConfig(config: NotificationConfig): Promise<void> {
        const url = Routes.addNotificationConfig();
        const resp = await ax.post(url, config, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return;
    }

    async listNotificationConfigs(): Promise<NotificationConfig[]> {
        const url = Routes.listNotificationConfigs();
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(r => NotificationConfig.fromJson(r));
    }

    async removeNotificationConfig(id: number): Promise<void> {
        const url = Routes.removeNotificationConfigs(id)
        await ax.post(url)
        return;
    }

    async saveNotificationConfig(config: NotificationConfig): Promise<void> {
        const url = Routes.saveNotificationConfig();
        await ax.post(url, config, this.contentTypeJsonConfig());
        return;
    }

    async addNotificationSensor(configId: number, sensor: NotificationSensor): Promise<void> {
        const url = Routes.addNotificationSensor(configId);
        await ax.post(url, sensor, this.contentTypeJsonConfig());
        return;
    }

    async removeNotificationSensor(id: number): Promise<void> {
        const url = Routes.removeNotificationSensor(id);
        await ax.post(url);
        return;
    }

    async setTerminalTechnicalCheckDoneTs(terminalGuidStr: string): Promise<void> {
        const url = Routes.setTerminalTechnicalCheckDone(terminalGuidStr);
        await ax.post(url);
        return;
    }

    async setHseGuideline(gl: HseGuideline): Promise<void> {
        const url = Routes.setHseGuideline();
        await ax.post(url, gl, this.contentTypeJsonConfig());
        return;
    }

    async listHseGuidelines(orgnum: number): Promise<HseGuideline[]> {
        const url = Routes.listHseGuideline(orgnum);
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(r => HseGuideline.fromJson(r));
    }

    // Delete given hse guideline. Returns txid for undo.
    async deleteHseGuideline(id: number): Promise<string> {
        const url = Routes.deleteHseGuideline(id);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return resp.data as string;
    }

    async getHseGuideline(locid: number): Promise<HseGuideline | null> {
        const url = Routes.getHseGuideline(locid);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp)
        if (resp.data) {
            return HseGuideline.fromJson(resp.data);
        }
        return null;
    }

    async confirmHseGuideline(id: number, version: number): Promise<void> {
        const url = Routes.confirmHseGuideline(id, version);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return;
    }

    async confirmedHseGuidelineReport(locid: number): Promise<PersonsHseGuidelineConfirmedReportResult> {
        const url = Routes.confirmedHseGuidelineReport(locid);
        const resp = await ax.get(url);
        PlayServerClient.assertJson(resp);
        return PersonsHseGuidelineConfirmedReportResult.fromJson(resp.data);
    }

    async fetchInfectiousPersonsLocations(personsAndIntervals: PersonRefAndDateInterval[]): Promise<TerminalLocationInfoDateInterval[]> {
        const url = Routes.fetchInfectiousPersonsLocations();
        const resp = await ax.post(url, personsAndIntervals, this.contentTypeJsonConfig());
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(r => TerminalLocationInfoDateInterval.fromJson(r));
    }

    async fetchInfectionTrace(locationDateIntervals: TerminalLocationInfoDateInterval[]): Promise<InfectionTraceRow[]> {
        const url = Routes.fetchInfectionTrace();
        const resp = await ax.post(url, locationDateIntervals, this.contentTypeJsonConfig());
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(r => InfectionTraceRow.fromJson(r));
    }

    async fetchAllTermlocsShortInfo(): Promise<TerminalLocationShortInfo[]> {
        const url = Routes.fetchAllTermlocsShortInfo();
        const resp = await ax.get(url);
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(r => TerminalLocationShortInfo.fromJson(r));
    }

    async xlsxInfectionTrace(personIntervals: GuiUserlistingAndDateInterval[], locationDateIntervals: TerminalLocationInfoDateInterval[]): Promise<void> {
        const url = Routes.xlsxInfectionTrace();
        const params = new InfectionTraceXlsxParams(personIntervals, locationDateIntervals);
        const options: any = this.contentTypeJsonConfig();
        options.responseType = "blob";
        const resp = await ax.post(url, params, options);
        // Hacky way of returning the result as a download.
        const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", "Anleggsterminal-smittesporing.xlsx");
        link.setAttribute("style", "display: none");
        document.body.appendChild(link);
        link.click();
        return;
    }

    async fetchPersonsMissingPhone(orgnum: number, from: DateTime, to: DateTime): Promise<PersonMissingPhoneReportRow[]> {
        const url = Routes.fetchPersonsMissingPhoneReport(orgnum);
        const params: any = {};
        params.from = from.toDayParam();
        params.to = to.toDayParam();
        const resp = await ax.get(url, {params: params});
        PlayServerClient.assertJsonArray(resp);
        return (resp.data as any[]).map(PersonMissingPhoneReportRow.fromJson);
    }

    async setFamocoStatus(deviceId: number, status: number): Promise<void> {
        const url = Routes.setFamocoStatus(deviceId, status);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        return;
    }

    async activateFamocoDevice(terminalGuid: string): Promise<ApiMessage | null> {
        const url = Routes.activateFamocoDevice(terminalGuid);
        const resp = await ax.post(url);
        PlayServerClient.assertJson(resp);
        if (PlayServerClient.hasResponseApiMessage(resp)) {
            return ApiMessage.fromJson(resp.data);
        }
        return null;
    }

    async triggerDataUseReport(terminalGuid: string): Promise<void> {
        const url = Routes.triggerDataUseReport(terminalGuid);
        await ax.post(url);
    }

    async getDataUseReport(terminalGuid: string): Promise<DataUseReport[]> {
        const url = Routes.getDataUseReport(terminalGuid);
        const resp = await ax.get(url);
        let data = resp.data;
        if(data instanceof Object && data.reports) {
            data = data.reports
        }
        if(data instanceof Array) {
            return DataUseReport.fromJsonArray(data);

        } else {
            throw new Error(`server returned json was not a "reports" array`)
        }
    }

    async setTerminalSoftDataLimits(terminalGuid: string, mobileHourlyMB: number, mobileMonthlyMB: number): Promise<void> {
        const url = Routes.setTerminalSoftDataLimits(terminalGuid);
        const resp = await ax.post(url, {mobileHourlyMB, mobileMonthlyMB}, this.contentTypeJsonConfig());
        PlayServerClient.assertJson(resp);
        return;
    }

    async isUserAppReviewPending(os: OS): Promise<boolean> {
        const url = Routes.isUserAppReviewPending(os.toString());
        const resp = await ax.get(url);
        return resp.data === true;
    }

    async userAppReviewTriggered(os: OS, triggerPoint: number): Promise<void> {
        const url = Routes.userAppReviewTriggered(os.toString());
        await ax.post(url, {triggerPoint}, this.contentTypeJsonConfig());
        return;
    }
    async recaptchaApproved(): Promise<boolean> {
        const url = Routes.recaptchaApproved();
        const resp = await ax.get(url);
        if("application/json" == PlayServerClient.contentType(resp)) {
            return resp.data === true;
        } else { // Got back the html from recaptcha redirect page
            return false;
        }
    }
}
